import { FormEvent, useState } from "react"
import { Head, useForm } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { ExamSettingsFields } from "@/components/ExamSettingsFields"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AccentPicker } from "@/components/AccentPicker"
import { LanguageSwitcher } from "@/components/LanguageSwitcher"
import { useIntl } from "react-intl"
import { FormRow, FormSection } from "@/components/blocks/FormRows"
import { ThemeToggle } from "@/components/ui/theme-toggle"
import { cn } from "@/lib/utils"
import type { SettingsProps } from "@/types/exam"

const ALL_TABS = [
  { id: "exam", label: { id: "settings.tab_exam" } },
  { id: "review", label: { id: "settings.tab_review" } },
  { id: "appearance", label: { id: "settings.tab_appearance" } },
] as const

type TabId = (typeof ALL_TABS)[number]["id"]

export default function Settings({
  exam_versions,
  exam_version_id,
  target_exam_date,
  daily_new_card_limit,
  program_access,
}: SettingsProps) {
  const intl = useIntl()
  // The exam tab only matters once a program is unlocked.
  const TABS = program_access ? ALL_TABS : ALL_TABS.filter((t) => t.id !== "exam")
  const [tab, setTab] = useState<TabId>(program_access ? "exam" : "review")

  const form = useForm({
    target_exam_date: target_exam_date ?? "",
    exam_version_id: exam_version_id ? String(exam_version_id) : "",
  })

  const reviewForm = useForm({ daily_new_card_limit: String(daily_new_card_limit) })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.patch("/settings", { preserveScroll: true, preserveState: true })
  }

  const submitReview = (e: FormEvent) => {
    e.preventDefault()
    reviewForm.patch("/settings", { preserveScroll: true, preserveState: true })
  }

  return (
    <>
      <Head title={intl.formatMessage({ id: "settings.title" })}>
        <meta name="description" content={intl.formatMessage({ id: "settings.meta" })} />
        <meta property="og:title" content={intl.formatMessage({ id: "settings.title" })} />
        <meta property="og:description" content={intl.formatMessage({ id: "settings.meta" })} />
      </Head>
      <AppShell>
        <PageHeader
          title={intl.formatMessage({ id: "settings.title" })}
          description={program_access ? intl.formatMessage({ id: "settings.subtitle_exam" }) : intl.formatMessage({ id: "settings.subtitle_general" })}
          tabs={
            <div role="tablist" aria-label={intl.formatMessage({ id: "settings.sections_label" })} className="flex items-end gap-6 border-b border-hairline">
              {TABS.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  id={`settings-tab-${id}`}
                  aria-selected={tab === id}
                  aria-controls={`settings-panel-${id}`}
                  tabIndex={tab === id ? 0 : -1}
                  onClick={() => setTab(id)}
                  onKeyDown={(e) => {
                    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return
                    const next = TABS[(TABS.findIndex((t) => t.id === id) + (e.key === "ArrowRight" ? 1 : -1) + TABS.length) % TABS.length]
                    setTab(next.id)
                    document.getElementById(`settings-tab-${next.id}`)?.focus()
                  }}
                  className={cn(
                    "-mb-px cursor-pointer border-b-2 px-1 py-3 text-sm",
                    tab === id
                      ? "border-accent font-medium text-accent-display"
                      : "border-transparent text-ink-body hover:text-ink-display",
                  )}
                >
                  {intl.formatMessage(label)}
                </button>
              ))}
            </div>
          }
        />

        {program_access && (
        <section
          role="tabpanel"
          id="settings-panel-exam"
          aria-labelledby="settings-tab-exam"
          hidden={tab !== "exam"}
          className="mt-8 max-w-3xl"
        >
          <form onSubmit={submit}>
            <FormSection title={intl.formatMessage({ id: "settings.exam_title" })} description={intl.formatMessage({ id: "settings.exam_description" })}>
              <div className="sm:py-6">
                <ExamSettingsFields
                  versions={exam_versions}
                  data={form.data}
                  setData={form.setData}
                  errors={form.errors}
                />
              </div>
            </FormSection>
            <div className="mt-6 flex justify-end">
              <Button type="submit" disabled={form.processing}>
                {intl.formatMessage({ id: "settings.save" })}
              </Button>
            </div>
          </form>
        </section>
        )}

        <section
          role="tabpanel"
          id="settings-panel-review"
          aria-labelledby="settings-tab-review"
          hidden={tab !== "review"}
          className="mt-8 max-w-3xl"
        >
          <form onSubmit={submitReview}>
            <FormSection title={intl.formatMessage({ id: "settings.review_title" })} description={intl.formatMessage({ id: "settings.review_description" })}>
              <FormRow
                label={intl.formatMessage({ id: "settings.new_cards_label" })}
                htmlFor="daily_new_card_limit"
                hint={intl.formatMessage({ id: "settings.new_cards_hint" })}
                hintId="daily_new_card_limit_help"
              >
                <Input
                  id="daily_new_card_limit"
                  type="number"
                  min={0}
                  max={200}
                  required
                  aria-invalid={!!reviewForm.errors.daily_new_card_limit}
                  aria-describedby="daily_new_card_limit_help"
                  value={reviewForm.data.daily_new_card_limit}
                  onChange={(e) => reviewForm.setData("daily_new_card_limit", e.target.value)}
                  className="max-w-32"
                />
                {reviewForm.errors.daily_new_card_limit && (
                  <p className="mt-2 text-xs text-danger-display">{reviewForm.errors.daily_new_card_limit}</p>
                )}
              </FormRow>
            </FormSection>
            <div className="mt-6 flex justify-end">
              <Button type="submit" disabled={reviewForm.processing}>
                {intl.formatMessage({ id: "settings.save" })}
              </Button>
            </div>
          </form>
        </section>

        <section
          role="tabpanel"
          id="settings-panel-appearance"
          aria-labelledby="settings-tab-appearance"
          hidden={tab !== "appearance"}
          className="mt-8 max-w-3xl"
        >
          <FormSection title={intl.formatMessage({ id: "settings.appearance_title" })} description={intl.formatMessage({ id: "settings.appearance_description" })}>
            <FormRow label={intl.formatMessage({ id: "settings.accent_label" })} hint={intl.formatMessage({ id: "settings.accent_hint" })}>
              <AccentPicker />
            </FormRow>
            <FormRow label={intl.formatMessage({ id: "language.label" })} hint={intl.formatMessage({ id: "language.hint" })}>
              <LanguageSwitcher className="sm:max-w-xs" />
            </FormRow>
            <FormRow label={intl.formatMessage({ id: "settings.theme_label" })}>
              <ThemeToggle />
            </FormRow>
          </FormSection>
        </section>
      </AppShell>
    </>
  )
}
